import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { RmsUser } from '../../models';
import { CrmUserService } from '../../../core/data-services/crm-user.service';
import { Domain } from '../../models/domain.model';
import { PaginatedList } from '../../../xform-compat';

@Component({
    selector: 'rms-crm-user-select',
    templateUrl: './crm-user-select.component.html',
    styleUrls: ['./crm-user-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => CrmUserSelectComponent)
        }
    ],
    standalone: false
})
export class CrmUserSelectComponent implements OnInit, OnChanges, ControlValueAccessor {

  @Input() selectedUsers: Array<RmsUser> = new Array<RmsUser>();
  @Output() selectedUsersChange: EventEmitter<any> = new EventEmitter<any>();

  @Input() selectedUser: RmsUser = new RmsUser();
  @Output() selectedUserChange: EventEmitter<any> = new EventEmitter<any>();

  @Input() label = 'Owners';
  @Input() placeholder = '';
  @Input() selectedDomains: Array<Domain> = [];
  @Input() multiple = true;
  @Input() required = true;
  @Input() clearable = true;

  public userList: Array<RmsUser> = new Array<RmsUser>();
  public domainMap: Map<number, Array<string>> = new Map<number, Array<string>>();
  public onChange;
  public loading = false;
  public disabled = false;

  constructor(
    private crmUserService: CrmUserService
  ) { }

  ngOnInit() {
    this.loadUsers();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedDomains) {
      this.userList = [...this.userList.sort((a, b) => this.userListCompare(a, b))];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    if (this.multiple) {
      if (!obj || obj instanceof Array) {
        this.selectedUsers = obj;
      } else {
        this.selectedUsers = [...this.selectedUsers, obj];
      }
    } else {
      this.selectedUser = obj;
    }
  }

  public searchFn(term: string, item: RmsUser): boolean {
    return item.fullName.toUpperCase().includes(term.toUpperCase());
  }

  public getDomainIcon(domainName: string) {
    return `${domainName.toLowerCase().replace(/ /g, '-')}.svg`;
  }

  private loadUsers() {
    this.loading = true;
    this.crmUserService.users.subscribe((resp: Array<RmsUser>) => {
      if (resp) {
        this.userList = resp.sort((a, b) => this.userListCompare(a, b));
        this.loadDomains();
      } else {
        this.userList = [];
      }
      this.loading = false;
    }, () => {
      this.userList = [];
      this.loading = false;
    });
  }

  private loadDomains() {
    this.loading = true;
    this.crmUserService.domains.subscribe((resp: PaginatedList<Domain>) => {
      this.loading = false;
      if (resp) {
        for (const domain of resp.records) {
          for (const user of domain.users) {
            if (!this.domainMap.has(user.id)) {
              this.domainMap.set(user.id, []);
            }
            this.domainMap.get(user.id).push(domain.name);
          }
        }
      } else {
        this.userList = [];
      }
    }, () => {
      this.userList = [];
      this.loading = false;
    });
  }

  private userListCompare(u1: RmsUser, u2: RmsUser): number {
    if (this.selectedDomains) {
      const u1InSelectedDomain = this.selectedDomains.some(g => g.users.some(u => u.id === u1.id));
      const u2InSelectedDomain = this.selectedDomains.some(g => g.users.some(u => u.id === u2.id));
      if (u1InSelectedDomain && !u2InSelectedDomain) {
        return -1;
      }
      if (!u1InSelectedDomain && u2InSelectedDomain) {
        return 1;
      }
    }

    return u1.lastName.toUpperCase().localeCompare(u2.lastName.toUpperCase());
  }

  onSelectionChange(value: any): void {
    if (this.multiple) {
      this.selectedUsers = value;
      this.selectedUsersChange.emit(value);
    } else {
      this.selectedUser = value;
      this.selectedUserChange.emit(value);
    }
  }

}
